import { isProduction } from "@/env";

// Logger interface
interface Logger {
	info(obj: object, msg?: string): void;
	info(msg: string): void;
	error(obj: object, msg?: string): void;
	error(msg: string): void;
	warn(obj: object, msg?: string): void;
	warn(msg: string): void;
	debug(obj: object, msg?: string): void;
	debug(msg: string): void;
	child(bindings: object): Logger;
}

// Simple console-based logger that works in all runtimes
function createLogger(bindings: object = {}): Logger {
	const baseContext = {
		service: "sigma-auth",
		environment: process.env.NODE_ENV || "development",
		version: process.env.npm_package_version || "1.0.0",
		...bindings,
	};

	function log(level: string, ...args: unknown[]) {
		const [first, second] = args;
		const msg = typeof first === "string" ? first : second;

		const logEntry = {
			level: level.toUpperCase(),
			time: new Date().toISOString(),
			...baseContext,
			...(typeof first === "object" && first !== null ? first : {}),
			...(msg ? { msg } : {}),
		};

		console.log(JSON.stringify(logEntry));
	}

	return {
		info: (...args: unknown[]) => log("info", ...args),
		error: (...args: unknown[]) => log("error", ...args),
		warn: (...args: unknown[]) => log("warn", ...args),
		debug: (...args: unknown[]) => log("debug", ...args),
		child: (childBindings: object) =>
			createLogger({ ...baseContext, ...childBindings }),
	} as Logger;
}

const logger = createLogger();

/**
 * Structured logger for OAuth operations
 */
/** @knipignore Referenced by name in tests' `mock.module("@/lib/logger")` stubs. */
export const oauthLogger = logger.child({
	component: "oauth",
});

/**
 * Structured logger for authentication operations
 */
export const authLogger = logger.child({
	component: "auth",
});

/**
 * Structured logger for API operations
 */
export const apiLogger = logger.child({
	component: "api",
});

/**
 * Structured logger for database operations
 */
export const dbLogger = logger.child({
	component: "database",
});

/**
 * Structured logger for agent-auth capability operations
 */
export const agentLogger = logger.child({
	component: "agent",
});

/**
 * Default logger for general use
 */
export { logger };

// Helper function to format errors for logging
export function formatError(error: Error | unknown) {
	return error instanceof Error
		? {
				message: error.message,
				stack: isProduction ? undefined : error.stack,
				name: error.name,
			}
		: { message: String(error) };
}
